'use client';
import Image from 'next/image';
import Cookies from 'js-cookie';
import { useState, useEffect } from 'react';
import { getZipcodeValidationStatus } from '@/app/api/zipcodeChecker/zipcodeChecker';
import offsetBlockClass from '@/utils/offsetBlockClass';
import clsx from 'clsx';

const BLOCKED_ZIP_PREFIXES = ['967', '968', '995', '996', '997', '998', '999'];

export default function ZipcodeChecker({ block, position }) {
  const {
    heading,
    input_placeholder_text,
    icon,
    show_offset_block_selector,
    uid
  } = block?.zipcode_checker?.reference?.[0] ?? {};

  const [zipcodeError, setZipcodeError] = useState(false);
  const [zipcodeMessage, setZipcodeMessage] = useState('');
  const [showZipcodeMessage, setShowZipcodeMessage] = useState(false);
  const [zipcodeNotAvailable, setZipcodeNotAvailable] = useState(false);
  const [editZipcode, setEditZipcode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [zipcode, setZipcode] = useState('');

  const showMessage = ({ notAvailable, message, showMessage, editZipcode }) => {
    setZipcodeNotAvailable(notAvailable);
    setZipcodeMessage(message);
    setShowZipcodeMessage(showMessage);
    setEditZipcode(editZipcode);
  };

  const fetchZipcodeData = async (zip) => {
    const res = await getZipcodeValidationStatus(zip);
    return res;
  };

  const handleZipcodeChange = (e) => {
    setZipcode(e.target.value);
    setZipcodeError(false);
  };

  const handleEditClick = () => {
    Cookies.remove('zipcode');
    setEditZipcode(true);
    setShowZipcodeMessage(false);
  };

  const handleZipCodeSubmit = async (e) => {
    e.preventDefault();

    const cleanedZipcode = zipcode.replace(/\s+/g, '');
    const isValidZip = /^\d{5}$/.test(cleanedZipcode);
    const prefix = cleanedZipcode.substring(0, 3);

    if (!isValidZip) {
      setZipcodeError(true);
      return;
    }
    if (BLOCKED_ZIP_PREFIXES.includes(prefix)) {
      showMessage({
        notAvailable: true,
        message: `We’re sorry,\nnot available in ${cleanedZipcode}!`,
        showMessage: true,
        editZipcode: false
      });
      return;
    }
    let result;

    try {
      result = await fetchZipcodeData(cleanedZipcode);
      if (!result.success) {
        showMessage({
          notAvailable: true,
          message: `We’re sorry,\nnot available in ${cleanedZipcode}!`,
          showMessage: true,
          editZipcode: false
        });
        return;
      }
      showMessage({
        notAvailable: false,
        message: `Available in ${cleanedZipcode}!\nAdd during checkout.`,
        showMessage: true,
        editZipcode: false
      });
      Cookies.set('zipcode', cleanedZipcode, { expires: 7 });
    } catch (error) {
      console.error('Error fetching zipcode data:', error);
      showMessage({
        notAvailable: true,
        message: `We’re sorry,\nnot available in ${cleanedZipcode}!`,
        showMessage: true,
        editZipcode: false
      });
    }
  };

  useEffect(() => {
    const storedZip = Cookies.get('zipcode');
    if (storedZip) {
      setZipcode(storedZip);
      fetchZipcodeData(storedZip)
        .then((res) => {
          if (!res || !res?.success) {
            setZipcodeNotAvailable(true);
            setShowZipcodeMessage(true);
            setZipcodeMessage(`We’re sorry,\nnot available in ${storedZip}!`);
          } else {
            setZipcodeNotAvailable(false);
            setZipcodeMessage(
              `Available in ${storedZip}!\nAdd during checkout.`
            );
          }
          setShowZipcodeMessage(true);
          setEditZipcode(false);
        })
        .catch((error) => {
          console.error('Error fetching stored zipcode data:', error);
          setZipcodeNotAvailable(true);
          setZipcodeMessage(`We’re sorry,\nnot available in ${storedZip}!`);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setEditZipcode(true);
      setLoading(false);
    }
  }, []);

  return (
    !loading && (
      <section
        id={`${uid}-${position}`}
        data-name="zipcode-checker"
        className={clsx(
          'group relative z-auto',
          offsetBlockClass(show_offset_block_selector, 'not-last:mb-8'),

          // This component has extra padding when in offset block mode
          // for when it's not followed by a multi-offset block
          show_offset_block_selector && 'not-has-[+_.multi-offset]:pb-6'
        )}
      >
        <form id="zipcode-checker-form" onSubmit={handleZipCodeSubmit}>
          {/* Start - Content block */}
          <div
            className={`flex flex-[1_0_0] flex-col items-start gap-2.5 border-b ${
              zipcodeError
                ? 'border-b-orange'
                : editZipcode
                  ? 'border-b-white'
                  : 'border-b-transparent'
            }`}
          >
            {/* Start - Header row with icon and title */}
            <div className="flex items-center gap-2">
              {icon?.url && (
                <Image
                  src={`${icon.url}?format=pjpg&auto=webp&disable=upscale`}
                  alt={icon?.title ?? heading ?? 'Zipcode Checker Icon'}
                  width={24.5}
                  height={20}
                />
              )}
              <p className="sh11 text-white">
                {heading ?? 'White Glove Delivery'}
              </p>
            </div>
            {/* End - Header row with icon and title */}
            {/* Start - Input or Success/Fail message */}
            {editZipcode && (
              <div className="flex h-12 items-center justify-between self-stretch pr-3 pl-5">
                <div className="relative w-full">
                  <input
                    placeholder={input_placeholder_text ?? 'Enter zip code'}
                    type="text"
                    inputMode="numeric"
                    data-dd-privacy="mask-user-input"
                    id="zipcodeCheckerEmail"
                    name="zipcodeCheckerEmail"
                    value={zipcode}
                    onChange={handleZipcodeChange}
                    className="body-s placeholder-dawn-grey-60 placeholder:body-s block w-full border-none bg-transparent p-0 outline-none focus:ring-0 focus:outline-none"
                  />
                  <button
                    type="submit"
                    aria-label="Submit Zip Code"
                    className={`absolute top-1/2 right-1 -translate-y-1/2 transform ${zipcodeError ? 'text-orange' : 'text-dawn-grey-60'}`}
                  >
                    <ArrowIcon />
                  </button>
                </div>
              </div>
            )}
            {(!editZipcode || showZipcodeMessage) && (
              <div className="flex h-12 items-center justify-between self-stretch">
                <div className="flex items-center gap-2 py-2">
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-full ${
                      zipcodeNotAvailable ? 'bg-dawn-grey' : 'bg-light-green'
                    }`}
                  >
                    {zipcodeNotAvailable ? <CloseIcon /> : <ZipCheckIcon />}
                  </div>
                  <p
                    className={`sh12 whitespace-pre-line ${
                      zipcodeNotAvailable
                        ? 'text-dawn-grey-60'
                        : 'text-light-green'
                    }`}
                  >
                    {zipcodeMessage}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleEditClick()}
                  className="body-r text-white underline"
                >
                  Edit zip code
                </button>
              </div>
            )}
            {/* End - Input or Success/Fail message */}
          </div>
          {/* End - Content block */}
          {zipcodeError && (
            <p
              className={clsx(
                'body-xs text-orange absolute right-0 left-0 z-10 mt-2',

                // Account for the case where you're in between multi-offset blocks
                show_offset_block_selector
                  ? 'bg-parlor-green-95 bottom-0.5 pb-0.5 pl-4 group-has-[+_.multi-offset]:-bottom-5'
                  : '-bottom-5'
              )}
            >
              Please enter a valid US zip code
            </p>
          )}
        </form>
      </section>
    )
  );
}

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 25 25"
    className="h-6 w-6 fill-current"
    aria-hidden="true"
  >
    <path d="m17.5 5.999-.707.707 5.293 5.293H1v1h21.086l-5.294 5.295.707.707L24 12.499l-6.5-6.5z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="38"
    height="38"
    viewBox="0 0 38 38"
    fill="none"
    aria-hidden="true"
  >
    <mask
      id="mask0_5594_14583"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="38"
      height="38"
    >
      <rect
        x="0.143799"
        y="19.0002"
        width="26.6667"
        height="26.6667"
        transform="rotate(-45 0.143799 19.0002)"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_5594_14583)">
      <path
        d="M12.8512 12.8514L25.1487 25.1489"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const ZipCheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_zip_check"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect x="0" y="0" width="24" height="24" fill="white" />
    </mask>
    <g mask="url(#mask0_zip_check)">
      <path
        d="M5.36108 12.1667L9.89812 17.0278L18.9722 7.30554"
        stroke="#0E3739"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
